import { computed, ref } from "vue";
import { defineStore } from "pinia";
import TrustSiteApi from "@dashboard/ajax/TrustSiteApi";
import type { RegionAliases } from "@entity/Region";
import type { NormalizedServiceReport, ServiceReport } from "@entity/ServiceReport";
import { deriveEventsBlock, extractRegionNames } from "@dashboard/utils/deriveEventsBlock";
import { deriveIncidents } from "@dashboard/utils/deriveIncidents";
import { normalizeReport } from "@dashboard/utils/normalizeReport";
import { createRequestGate } from "@dashboard/utils/requestGate";

export type { RegionAliases };

function getErrorMessage(err: unknown): string {
  if (err instanceof Error) {
    return err.message;
  }

  return String(err);
}

/**
 * Stores report data and handles individual report requests.
 * useDashboardSession owns polling so every scheduled refresh includes brand banners.
 * isLoading/error cover this request and report normalization only; the session
 * exposes status for the complete screen loading cycle.
 * A stale or abandoned request must not call setReport or clear a newer fetch's loading flag.
 */
export const useDashboardStore = defineStore("dashboard", () => {
  const report = ref<NormalizedServiceReport>([]);
  const regionAliases = ref<RegionAliases>({});
  const regionNames = ref<string[]>([]);
  const isLoading = ref(false);
  const error = ref<string | null>(null);
  const reportRequests = createRequestGate();

  const hasReport = computed(() => report.value.length > 0);
  const hasRegions = computed(() => regionNames.value.length > 0);
  const incidents = computed(() => deriveIncidents(report.value, regionAliases.value));
  const eventsBlockReport = computed(() =>
    deriveEventsBlock(report.value, regionAliases.value, regionNames.value));

  function startReportRequest(): number {
    const requestId = reportRequests.start();
    error.value = null;
    isLoading.value = true;
    return requestId;
  }

  function abandonReportRequest(requestId: number): void {
    if (!reportRequests.abandon(requestId)) {
      return;
    }

    isLoading.value = false;
  }

  function setReport(nextReport: ServiceReport): void {
    // Prepare everything before publishing: invalid data leaves the previous state intact.
    const normalizedReport = normalizeReport(nextReport);
    // The legacy getter retains the previous region headings for an empty report.
    const nextRegionNames = normalizedReport.length > 0
      ? extractRegionNames(normalizedReport, regionAliases.value)
      : regionNames.value;

    report.value = normalizedReport;
    regionNames.value = nextRegionNames;
  }

  function setRegionAliases(nextAliases: RegionAliases): void {
    regionAliases.value = nextAliases;
    if (report.value.length > 0) {
      regionNames.value = extractRegionNames(report.value, regionAliases.value);
    }
  }

  async function fetchReport(
    api: TrustSiteApi = new TrustSiteApi(),
    requestId = startReportRequest(),
  ): Promise<boolean> {
    try {
      const response = await api.service().statusStatic();
      if (!reportRequests.isCurrent(requestId)) {
        return false;
      }

      // Vue 2 also treated a resolved non-array as a successful load (no redirect).
      // The page looked empty because incidents/eventsBlockReport then threw
      // TypeError during render (forEach/reduce/length). Here the load is still
      // successful and the screen is empty, but report is [] so getters do not
      // crash; the console gets this explicit error instead of a render TypeError.
      if (!Array.isArray(response)) {
        console.error(
          "Can't read status dashboard report: statusStatic() returned a non-array",
          response,
        );
        setReport([]);
        return true;
      }

      setReport(response);
      return true;
    } catch (err) {
      if (!reportRequests.isCurrent(requestId)) {
        return false;
      }

      error.value = getErrorMessage(err);
      throw err;
    } finally {
      if (reportRequests.isCurrent(requestId)) {
        isLoading.value = false;
      }
    }
  }

  function $reset(): void {
    reportRequests.invalidate();
    report.value = [];
    regionAliases.value = {};
    regionNames.value = [];
    isLoading.value = false;
    error.value = null;
  }

  return {
    report,
    regionAliases,
    regionNames,
    isLoading,
    error,
    hasReport,
    hasRegions,
    incidents,
    eventsBlockReport,
    setReport,
    setRegionAliases,
    startReportRequest,
    abandonReportRequest,
    fetchReport,
    $reset,
  };
});
